import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Package, Eye, EyeOff, Check } from "lucide-react";
import { API, formatApiErrorDetail } from "@/lib/api";

export default function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail ? formatApiErrorDetail(err.response.data.detail) : "Reset failed. Try requesting a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-md border border-zinc-200 rounded-none shadow-none">
        <CardHeader className="p-6 pb-4 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <Package className="w-10 h-10 text-orange-600" strokeWidth={2} />
            <h1 className="text-2xl font-bold text-zinc-900 tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              Pallet Cut Calculator
            </h1>
          </div>
          <p className="text-sm text-zinc-500">{done ? "Password updated" : "Set a new password"}</p>
        </CardHeader>
        <CardContent className="p-6 pt-0">
          {done ? (
            <div className="text-center space-y-4">
              <div className="w-12 h-12 mx-auto bg-green-100 flex items-center justify-center">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm text-zinc-600">Your password has been reset. Sign in with your new password.</p>
              <Button onClick={() => { window.location.href = "/"; }}
                className="w-full rounded-none bg-orange-600 hover:bg-orange-700 text-white font-semibold uppercase tracking-wider"
                data-testid="reset-go-login-btn">
                Go to Sign In
              </Button>
            </div>
          ) : !token ? (
            <p className="text-sm text-red-600 text-center">This reset link is invalid. Request a new one from the sign-in page.</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">New Password</Label>
                <div className="relative mt-1">
                  <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password" required minLength={4}
                    className="rounded-none border-zinc-300 pr-10" data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">Confirm Password</Label>
                <Input type={showPassword ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat new password" required minLength={4}
                  className="mt-1 rounded-none border-zinc-300" data-testid="reset-confirm-input" />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
              <Button type="submit" disabled={loading}
                className="w-full rounded-none bg-orange-600 hover:bg-orange-700 text-white font-semibold uppercase tracking-wider"
                data-testid="reset-submit-btn">
                {loading ? "..." : "Reset Password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
